import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { useState, useRef, useEffect } from 'react';
import InterestIndicator from './InterestIndicator';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import { useAuth } from './auth-provider';
import NinjaLogo from './NinjaLogo';
import { cleanupAuthState } from '@/lib/supabase';

interface HeaderProps {
  conversationTitle?: string;
  interestScore?: number;
  loadingScores?: boolean;
  onSignOut?: () => void;
  onToggleSidebar?: () => void;
  currentPath: string;
}

export default function Header({
  conversationTitle,
  interestScore,
  loadingScores = false,
  onSignOut,
  onToggleSidebar,
  currentPath
}: HeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const supabase = createClientComponentClient();
  const { isAuthenticated, refreshAuth, isLoading } = useAuth();
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const sidebarToggleRef = useRef<HTMLButtonElement>(null);
  const [lastRefreshPath, setLastRefreshPath] = useState<string>('');
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialMountRef = useRef(true);

  // Only refresh auth when path significantly changes 
  // Skip the direct auth check on mount to avoid redundant refreshes
  useEffect(() => {
    // Skip on initial mount since auth-provider will handle initial auth check
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }

    // Prevent refreshes happening too close together
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Only refresh auth if the path has changed from the last refresh
    // and we're not just toggling loading state
    if (pathname !== lastRefreshPath) {
      console.log('Path changed, will refresh auth state:', pathname);
      
      // Add a small delay to prevent multiple rapid calls
      refreshTimeoutRef.current = setTimeout(async () => {
        await refreshAuth();
        setLastRefreshPath(pathname);
      }, 300);
    }

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [pathname, lastRefreshPath, refreshAuth]);

  // Log auth state for debugging (only when state changes, not on every render)
  useEffect(() => {
    console.log('Header auth state:', isAuthenticated, 'Loading:', isLoading, 'Path:', pathname);
  }, [isAuthenticated, isLoading, pathname]);

  // Handle clicking outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        !menuRef.current.contains(event.target as Node) &&
        burgerRef.current &&
        !burgerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      console.log("Log out initiated");
      if (onSignOut) {
        onSignOut();
      } else {
        // Sign out using the auth-helpers client
        const { error } = await supabase.auth.signOut();
        
        if (error) {
          console.error("Error during sign out:", error.message);
          return;
        }
        
        console.log("Supabase signOut completed successfully");
        
        // Use the helper to clean up auth state
        cleanupAuthState();
        
        // Refresh auth state to update the UI
        await refreshAuth();
        console.log("Auth state refreshed after logout");
        
        // Redirect to login page
        console.log("Redirecting to login page");
        router.push('/login');
      }
    } catch (err) {
      console.error("Unexpected error during sign out:", err);
      router.push('/login');
    }
  };

  // Determine if we're in a conversation view
  const isConversationView = currentPath.includes('/conversations/') && !currentPath.includes('/new');

  // Desktop navigation links
  const renderDesktopNavLinks = () => {
    return (
      <>
        {isAuthenticated && (
          <>
            <Link
              href="/conversations"
          className={`hidden md:inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg ${
            currentPath === '/conversations' 
              ? 'bg-primary/90 text-primary-foreground hover:bg-primary' 
              : 'hover:bg-secondary/50'
          } transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
          </svg>
          <span>Conversations</span>
        </Link>
        
        <Link
          href="/bio-generator"
          className={`hidden md:inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg ${
            currentPath === '/bio-generator' 
              ? 'bg-primary/90 text-primary-foreground hover:bg-primary' 
              : 'hover:bg-secondary/50'
          } transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>Bio Generator</span>
        </Link>
        
        <Link
          href="/picture-analysis"
          className={`hidden md:inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg ${
            currentPath === '/picture-analysis' 
              ? 'bg-primary/90 text-primary-foreground hover:bg-primary' 
              : 'hover:bg-secondary/50'
          } transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <span>Picture Analysis</span>
        </Link>
        
        <Link
          href="/account"
          className={`hidden md:inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg ${
            currentPath === '/account' 
              ? 'bg-primary/90 text-primary-foreground hover:bg-primary' 
              : 'hover:bg-secondary/50'
          } transition-colors`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Account</span>
        </Link>
          </>
        )}
        
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span>Docs</span>
        </a>
      </>
    );
  };
  
  // Mobile navigation links
  const renderMobileNavLinks = () => {
    return (
      <>
        {isAuthenticated && (
          <>
            <Link
              href="/conversations"
          className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors"
          onClick={closeMenu}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
          </svg>
          <span className="font-medium text-white">Conversations</span>
        </Link>
        
        <Link
          href="/bio-generator"
          className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors"
          onClick={closeMenu}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="font-medium text-white">Bio Generator</span>
        </Link>
        
        <Link
          href="/picture-analysis"
          className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors"
          onClick={closeMenu}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <span className="font-medium text-white">Picture Analysis</span>
        </Link>
        
        <Link
          href="/account"
          className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors"
          onClick={closeMenu}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium text-white">Account</span>
        </Link>
          </>
        )}
        
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center px-4 py-3 hover:bg-gray-700 transition-colors"
          onClick={closeMenu}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
          <span className="font-medium text-white">Documentation</span>
        </a>
      </>
    );
  };

  // If auth is still loading and we're on a protected route, show minimal header
  if (isLoading && (currentPath !== '/login' && currentPath !== '/signup')) {
    return (
      <header className="sticky top-0 z-10 border-b border-border/40 glassmorphic-heavy">
        <div className="container flex h-16 items-center px-4 justify-center">
          <div className="flex items-center gap-2">
            <NinjaLogo size="1.75rem" />
            <h1 className="text-2xl font-bold tracking-tight">DateHack App</h1>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-10 border-b border-border/40 glassmorphic-heavy">
      <div className="container flex h-16 items-center px-4">
        {/* Conversations Toggle for Mobile */}
        {isAuthenticated && isConversationView && onToggleSidebar && (
          <button
            ref={sidebarToggleRef}
            onClick={onToggleSidebar}
            className="md:hidden p-2 rounded-lg hover:bg-secondary/50 transition-colors focus:outline-none"
            aria-label="Conversations"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </button>
        )}

        {/* App Title or Conversation Title */}
        <div className="flex-1 flex items-center justify-center gap-2">
          {isAuthenticated && isConversationView ? (
            <>
              <h1 className="text-xl font-bold truncate text-center">
                {conversationTitle || 'Conversation'}
              </h1>
              
              {/* Interest indicator next to match name */}
              <div className="flex items-center ml-2">
                <div className="w-[95px] md:w-[112px]">
                  <InterestIndicator
                    interestScore={interestScore !== undefined ? interestScore : 25}
                    isLoading={loadingScores}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <NinjaLogo size="1.75rem" />
              <h1 className="text-2xl font-bold tracking-tight">DateHack App</h1>
            </div>
          )}
        </div>
        
        {/* Navigation Links */}
        <div className="ml-auto flex items-center gap-2">
          {/* Desktop menu */}
          {renderDesktopNavLinks()}
          
          {/* Non-authenticated links */}
          {!isAuthenticated && (
            <>
              <Link
                href="/login"
                className="hidden md:inline-flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                <span>Log in</span>
              </Link>
            </>
          )}
          
          {/* Burger menu for mobile */}
          <button 
            ref={burgerRef}
            onClick={toggleMenu} 
            className="md:hidden p-2 rounded-lg hover:bg-secondary/50 transition-colors focus:outline-none"
            aria-label="Menu"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <line x1="4" x2="20" y1="12" y2="12" />
              <line x1="4" x2="20" y1="6" y2="6" />
              <line x1="4" x2="20" y1="18" y2="18" />
            </svg>
          </button>
          
          {/* Mobile dropdown menu */}
          {isMenuOpen && (
            <div 
              ref={menuRef}
              className="md:hidden fixed top-16 right-4 w-72 rounded-xl shadow-xl overflow-hidden z-20 bg-gray-800 border border-gray-700"
            >
              <div className="py-2 divide-y divide-gray-700">
                {/* Mobile navigation links */}
                {renderMobileNavLinks()}
                {!isAuthenticated && (
                  <>
                    <Link
                      href="/login"
                      className="flex items-center gap-1.5 text-sm font-medium w-full px-3 py-2 rounded-lg hover:bg-secondary/50 transition-colors"
                      onClick={closeMenu}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <span>Log in</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
} 